import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography, Checkbox, FormControlLabel, Paper, Button, CircularProgress, Skeleton } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { colors } from '../../theme/colors';
import { useDescriptionItems } from '../../hooks/useDescriptionItems';

interface DescriptionChecklistProps {
  /** Unique step ID for caching extraction results */
  stepId: string;
  /** The raw description text */
  description: string;
  /** Optional: pass checked state from parent for persistence */
  checkedItems?: Set<string>;
  /** Optional: callback when an item is checked/unchecked */
  onCheckChange?: (item: string, checked: boolean) => void;
  /** Optional: callback when all items are checked/unchecked */
  onCheckAllChange?: (items: string[], checked: boolean) => void;
  /** Whether the step is completed - affects styling */
  isCompleted?: boolean;
  /** Text color override */
  textColor?: string;
}

/**
 * Renders a step description with optional checklist.
 * 
 * Uses AI to extract list items on-demand (with caching).
 * If no items found, renders the description as plain text.
 */
export const DescriptionChecklist: React.FC<DescriptionChecklistProps> = ({
  stepId,
  description,
  checkedItems: externalCheckedItems,
  onCheckChange,
  onCheckAllChange,
  isCompleted = false,
  textColor,
}) => {
  // Local state for checked items (used when no external state provided)
  const [localCheckedItems, setLocalCheckedItems] = useState<Set<string>>(new Set());
  
  // Use external state if provided, otherwise local
  const checkedItems = externalCheckedItems ?? localCheckedItems;
  
  // Use AI extraction with caching
  const { items: listItems, hasItems, loading, ready } = useDescriptionItems(stepId, description);
  
  // Calculate if all items are checked
  const allChecked = useMemo(() => {
    if (listItems.length === 0) return false;
    return listItems.every(item => checkedItems.has(item));
  }, [listItems, checkedItems]);

  // Calculate checked count for display
  const checkedCount = useMemo(() => {
    return listItems.filter(item => checkedItems.has(item)).length;
  }, [listItems, checkedItems]);

  // Handle checkbox toggle
  const handleToggle = useCallback((item: string) => {
    const isChecked = checkedItems.has(item);
    const newChecked = !isChecked;
    
    if (onCheckChange) {
      onCheckChange(item, newChecked);
    } else {
      // Update local state
      setLocalCheckedItems(prev => {
        const next = new Set(prev);
        if (newChecked) {
          next.add(item);
        } else {
          next.delete(item);
        }
        return next;
      });
    }
  }, [checkedItems, onCheckChange]);

  // Handle check all / uncheck all
  const handleCheckAll = useCallback(() => {
    const shouldCheckAll = !allChecked;
    
    if (onCheckAllChange) {
      onCheckAllChange(listItems, shouldCheckAll);
    } else {
      // Update local state
      setLocalCheckedItems(prev => {
        if (shouldCheckAll) {
          // Add all items - use Array.from for ES5 compatibility
          const combined = Array.from(prev).concat(listItems);
          return new Set(combined);
        } else {
          // Remove all items from this list
          const next = new Set(prev);
          listItems.forEach(item => next.delete(item));
          return next;
        }
      });
    }
  }, [allChecked, listItems, onCheckAllChange]);

  const effectiveTextColor = textColor ?? (isCompleted ? colors.gray[500] : colors.gray[600]);

  // Show loading indicator while extracting
  if (loading && !ready) {
    return (
      <Box>
        {/* Show description text while analyzing */}
        <Typography
          variant="body2"
          sx={{
            color: effectiveTextColor,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            mb: 1.5,
          }}
        >
          {description}
        </Typography>
        
        {/* AI analyzing indicator */}
        <Paper
          variant="outlined"
          sx={{
            bgcolor: colors.blue[50],
            borderColor: colors.blue[200],
            borderRadius: 1.5,
            overflow: 'hidden',
            p: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: colors.blue[100],
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 16, color: colors.blue[600] }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.blue[700],
                    fontWeight: 600,
                  }}
                >
                  Making this easier to check off...
                </Typography>
                <CircularProgress size={12} sx={{ color: colors.blue[500] }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Skeleton variant="rounded" width={80} height={20} sx={{ bgcolor: colors.blue[100] }} />
                <Skeleton variant="rounded" width={100} height={20} sx={{ bgcolor: colors.blue[100] }} />
                <Skeleton variant="rounded" width={60} height={20} sx={{ bgcolor: colors.blue[100] }} />
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  // If no list items, render as plain text
  if (!hasItems || listItems.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: effectiveTextColor,
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
        }}
      >
        {description}
      </Typography>
    );
  }

  // Render with checklist
  return (
    <Box>
      {/* Description text as preamble */}
      <Typography
        variant="body2"
        sx={{
          color: effectiveTextColor,
          lineHeight: 1.6,
          mb: 1.5,
        }}
      >
        {description}
      </Typography>

      {/* Checklist items */}
      <Paper
        variant="outlined"
        sx={{
          bgcolor: colors.gray[50],
          borderColor: colors.gray[200],
          borderRadius: 1.5,
          overflow: 'hidden',
        }}
      >
        {/* Check all header - only show for lists with 3+ items */}
        {listItems.length >= 3 && !isCompleted && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5,
              py: 1,
              borderBottom: `1px solid ${colors.gray[200]}`,
              bgcolor: colors.gray[100],
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.gray[600],
                fontWeight: 500,
              }}
            >
              {checkedCount} of {listItems.length} completed
            </Typography>
            <Button
              size="small"
              onClick={handleCheckAll}
              startIcon={allChecked ? <CheckBoxIcon sx={{ fontSize: 16 }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                py: 0.25,
                px: 1,
                color: allChecked ? colors.green[700] : colors.blue[600],
                '&:hover': {
                  bgcolor: allChecked ? colors.green[50] : colors.blue[50],
                },
              }}
            >
              {allChecked ? 'Uncheck all' : 'Check all'}
            </Button>
          </Box>
        )}

        <Box
          component="ul"
          sx={{
            m: 0,
            p: 0,
            listStyle: 'none',
          }}
        >
          {listItems.map((item, index) => {
            const isChecked = checkedItems.has(item);
            const isLast = index === listItems.length - 1;

            return (
              <Box
                component="li"
                key={`${item}-${index}`}
                sx={{
                  borderBottom: isLast ? 'none' : `1px solid ${colors.gray[200]}`,
                  '&:hover': {
                    bgcolor: isCompleted ? 'transparent' : colors.blue[50],
                  },
                  transition: 'background-color 0.15s ease',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isChecked || isCompleted}
                      onChange={() => handleToggle(item)}
                      disabled={isCompleted}
                      size="small"
                      sx={{
                        color: colors.gray[400],
                        '&.Mui-checked': {
                          color: colors.green[600],
                        },
                        '&.Mui-disabled': {
                          color: colors.green[200],
                        },
                        p: 0.75,
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{
                        color: (isChecked || isCompleted) ? colors.gray[500] : colors.gray[700],
                        textDecoration: (isChecked || isCompleted) ? 'line-through' : 'none',
                        lineHeight: 1.5,
                        py: 0.25,
                      }}
                    >
                      {item}
                    </Typography>
                  }
                  sx={{
                    mx: 0,
                    px: 1.5,
                    py: 0.75,
                    width: '100%',
                    alignItems: 'flex-start',
                    '& .MuiFormControlLabel-label': {
                      flex: 1,
                    },
                  }}
                />
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default DescriptionChecklist;
